import { intercomService } from "@services/intercomService";
import {
  copyText,
  WSButton,
  WSCard,
  WSFlexBox,
  WSList,
  WSText
} from "@wingspanhq/fe-component-library";
import { InvoiceStatus } from "@wingspanhq/wingspan-api-sdk";
import React from "react";
import { RouteComponentProps, useHistory } from "react-router-dom";
import { addNotification } from "../../components/Notification/Notification";
import { queriesInvoices, queriesPayerRows, queriesPayers } from "@query";
import { selectorSearchRowName } from "../../shared/selectors/selectorSearchRowName";
import { AlertEnableTaxWithholdings } from "../../modules/TaxWithholdings/components/AlertEnableTaxWithholdings";
import { WSQuery } from "../../query/WSQuery";
import { useMutationDownloadInvoicePdf } from "../../query/payments/mutations";
import { useQueryOwnPayoutSettings } from "@hooks/useQueryOwnPayoutSettings";
import { Layout } from "../../shared/components/Layout";
import { FirstInvoiceBanner } from "../components/FirstInvoiceBanner/FirstInvoiceBanner";
import {
  InvoiceStatusTimeline,
  InvoiceTimelineStatus
} from "../components/InvoiceStatusTimeline/InvoiceStatusTimeline";
import { InvoiceTotals } from "../components/InvoiceTotals/InvoiceTotals";
import { useInvoiceFormGoBack } from "../utils/invoicesFormUtils";

const COPY_LINK_FAILED_MESSAGE =
  "We couldn't create the invoice link. Please try again.";

export const RouteInvoiceConfirmation: React.FC<
  RouteComponentProps<{
    invoiceId: string;
  }>
> = ({
  match: {
    params: { invoiceId }
  }
}) => {
  const history = useHistory();
  const queryInvoice = queriesInvoices.getInvoice.useQuery({ invoiceId });
  // Both payer reads are presentation-only — a name in the card heading and a
  // contact email in one sentence — and neither can start until the invoice
  // resolves, because both are keyed on its `payerId`. Gating the screen on
  // them made the confirmation of an already-completed send strictly serial:
  // one round trip for the invoice, a second for the payer, spinner throughout.
  // So the screen below waits on the invoice alone and lets these fill in when
  // they land. Both are `retry: false`, so a failure settles fast and just
  // leaves the fallback copy in place instead of blanking the screen.
  //
  // The displayed name comes off the search row's server-resolved
  // `profile.resolvedName`, so it matches the name the invoice tables show for
  // this client.
  const queryPayer = queriesPayers.getPayer.useQuery(
    { payerId: queryInvoice.data?.payerId! },
    {
      enabled: !!queryInvoice.data?.payerId,
      retry: false
    }
  );
  const queryPayerRow = queriesPayerRows.searchPayerRows.useQuery(
    {
      filter: { id: { eq: queryInvoice.data?.payerId! } },
      page: { size: 1 }
    },
    {
      enabled: !!queryInvoice.data?.payerId,
      retry: false
    }
  );
  // Warm-up, deliberately unused here: `InvoiceTotals` in the summary card has
  // its own gate on this query, and it can only start once that component
  // mounts — i.e. after the invoice lands. Requesting it here puts it in flight
  // alongside the invoice so the summary isn't a third serial hop. React Query
  // dedupes on the key, so `InvoiceTotals` reads the same cache entry.
  useQueryOwnPayoutSettings();

  const [downloadInvoicePdf, downloadInvoicePdfMeta] =
    useMutationDownloadInvoicePdf();
  const [createInvoiceSecureLink, createInvoiceSecureLinkMeta] =
    queriesInvoices.createInvoiceSecureLink.useMutation();
  // `useWSMutation` defaults `throwOnError` to false, so a failed call resolves
  // with `undefined`: without the `else`/`onError` below, a 503 (PLA-3525) and a
  // 200 carrying no url both left the button looking like it did nothing
  // (PLA-3531).
  const copyInvoiceLink = () => {
    createInvoiceSecureLink(
      { invoiceId },
      {
        onSuccess: link => {
          if (link?.url) {
            copyText(link.url);
            addNotification({ text: "Invoice link copied to clipboard." });
          } else {
            addNotification({
              text: COPY_LINK_FAILED_MESSAGE,
              timeout: 6000
            });
          }
        },
        onError: () => {
          addNotification({ text: COPY_LINK_FAILED_MESSAGE, timeout: 6000 });
        }
      }
    );
  };
  const goBack = useInvoiceFormGoBack();

  return (
    <Layout
      onClose={goBack}
      title={
        queryInvoice.data
          ? `Invoice #${queryInvoice.data?.invoiceNumber}`
          : "Invoice"
      }
    >
      <WSQuery query={queryInvoice}>
        {({ data: invoice }) => {
          const payerRow = queryPayerRow.data?.data?.[0];
          // Empty until the row lands (and if it fails, or resolves to nothing).
          // An absent name reads as absent, not as broken, so it degrades on its
          // own — unlike the sentence below.
          const payerName = payerRow ? selectorSearchRowName(payerRow) : "";
          // TODO (v3-migration): `payer.email` comes back empty for active
          // payers, so the search row's email is tried next. When neither is
          // available — that gap, a failed read, or simply not loaded yet — the
          // sentence names no address rather than trailing off after "Sent to".
          const payerEmail = queryPayer.data?.email || payerRow?.email;
          const recipient = payerEmail ? `to ${payerEmail}` : "to your client";
          const deliveryText = !invoice.notificationPreferences
            ?.shouldSendInvoice
            ? "Use your invoice link to send your payment requests directly to clients through your existing communication channel."
            : invoice.status === InvoiceStatus.PENDING
              ? `Will be sent ${recipient}`
              : `Sent ${recipient}`;

          return (
            <WSList gap="XL">
              <FirstInvoiceBanner />

              <WSCard>
                <WSFlexBox justify="space-between" wrap="nowrap">
                  <WSText.Heading4 mb="M">{payerName}</WSText.Heading4>
                  <WSButton.Link
                    onClick={() => {
                      downloadInvoicePdf({
                        invoiceId: invoice.id!
                      });
                    }}
                    loading={downloadInvoicePdfMeta.isLoading}
                  >
                    Download PDF
                  </WSButton.Link>
                </WSFlexBox>

                <WSText.Heading5 mb="XS">
                  {invoice.status === InvoiceStatus.PENDING
                    ? "Invoice is pending"
                    : invoice.notificationPreferences?.shouldSendInvoice
                      ? "Your invoice has been sent"
                      : "Your invoice has been opened"}
                </WSText.Heading5>
                <WSText color="gray500" mb="XL">
                  <WSText>{deliveryText}</WSText>
                  {invoice.status === InvoiceStatus.PENDING && (
                    <WSButton.Link my="M" onClick={intercomService.open}>
                      Ask about pending invoices
                    </WSButton.Link>
                  )}
                </WSText>
                <InvoiceStatusTimeline
                  currentStatus={
                    invoice.status === InvoiceStatus.PENDING
                      ? undefined
                      : "Sent"
                  }
                  statuses={[
                    InvoiceTimelineStatus.Sent,
                    InvoiceTimelineStatus.Viewed,
                    InvoiceTimelineStatus.Paid,
                    InvoiceTimelineStatus.Deposited
                  ]}
                  mb="XL"
                />
                <WSButton
                  fullWidth
                  mr="M"
                  name="copyInvoiceLink"
                  kind="Secondary"
                  loading={createInvoiceSecureLinkMeta.isLoading}
                  onClick={copyInvoiceLink}
                >
                  Copy invoice link
                </WSButton>
              </WSCard>

              <WSCard>
                <WSText.Heading5 mb="M" data-testid="paymentTimingText">
                  Payment timing
                </WSText.Heading5>
                <WSText color="gray600">
                  Funds are automatically deposited within 7 business days of
                  payment.
                </WSText>
              </WSCard>

              <WSCard>
                <WSList gap="2XL">
                  <WSText.Heading5>Summary</WSText.Heading5>

                  <InvoiceTotals invoice={invoice} />

                  <AlertEnableTaxWithholdings />

                  <WSButton
                    onClick={() => {
                      history.push("/member/invoices");
                    }}
                    fullWidth
                  >
                    Back to invoices
                  </WSButton>
                </WSList>
              </WSCard>
            </WSList>
          );
        }}
      </WSQuery>
    </Layout>
  );
};
